<template>
  <input type="text" :value="actualValue" :placeholder="placeholderText" @input="onInput($event)" class="flatpickr-input" :aria-required="required" :aria-describedby="describedBy">
</template>

<script lang="ts">
  import Vue from "vue";
  import Flatpickr from "flatpickr";
  import {DatetimeMode} from "../models/datetimeMode";
  import {TimeFormat} from '../models/timeFormat';
  import moment from 'moment-timezone';
  import DatetimeHelper from "../helpers/datetimeHelper";
  import InputMasker from "~/helpers/inputMasker";
  import storeMapper from "~/mixins/storeMappers/storeMapper";

  interface Minimum {
    minDate: string;
  }

  interface Maximum {
    maxDate: string;
  }

  const timeZoneStoreMixin = storeMapper('timezoneStore', {state: ['timezone']});

  export default Vue.extend({
    mixins: [
      timeZoneStoreMixin
    ],
    props: {
      value: {
        type: String
      },
      mode: {
        type: Number as () => DatetimeMode
      },
      min: {
        type: String,
        default: null
      },
      max: {
        type: String,
        default: null
      },
      placeholder: {
        type: String,
        default: null,
      },
      minuteIncrement: {
        type: Number,
        default: 5,
      },
      allowInput: {
        type: Boolean,
        default: true
      },
      timezoneOverride: {
        type: String,
        required: false,
        default: null
      },
      timeFormat: {
        type: Number as () => TimeFormat,
        default: TimeFormat.TwelveHour
      },
      parseFormat: {
        type: String,
        default: DatetimeHelper.parseFormat,
      },
      required: {
        type: Boolean,
        default: false,
      },
      describedBy: {
        type: String,
      },
    },
    data: function() {
      return {
        fp: null,
        actualValue: '',
      }
    },
    beforeMount() {
      // Add conditional to prevent warning when trying to create moment objects with nil values
      if (!this.value) return

      if (this.mode === DatetimeMode.Date || this.mode === DatetimeMode.Time) {
        this.actualValue = moment.utc(this.value).format('YYYY-MM-DD HH:mm:ss');
      } else {
        this.actualValue = moment.tz(this.value, this.timezoneOverride || this.timezone).format('YYYY-MM-DD HH:mm:ssZ');
      }
    },
    mounted() {
      if (this.mode in DatetimeMode) {
        this.mountPicker();
        InputMasker.initializeInputMasker(this.fp.altInput);

        // little hack to get #175773530 resolved
        const that = this;
        $(that.fp.altInput).on('input', function(e) {
          if (e.target.value.trim().length === 0) {
            that.fp.clear();
            $(that.fp.altInput).trigger('iMaskInput');
          }
        })
      }
    },
    beforeDestroy() {
      if (!this.fp) return;
      this.destroy();
    },
    methods: {
      mountPicker() {
        let config = {
          altInput: true,
          altInputClass: '',
          altFormat: this.altFormat,
          ariaDateFormat: this.ariaDateFormat,
          disableMobile: true, // disables the browser's default datepicker for mobile - clashes with Flatpickr
          minuteIncrement: this.minuteIncrement,
          minDate: this.min,
          maxDate: this.max,
          defaultDate: this.actualValue,
          dateFormat: this.parseFormat,
          timeFormat: this.timeFormat,
          time_24hr: this.timeFormat === TimeFormat.TwentyFourHour,
          parseDate: DatetimeHelper.parseDateFactory(this.parseTimezone, this.parseFormat),
          formatDate: DatetimeHelper.formatDateFactory(this.parseTimezone),
          parseTimezone: this.timezoneOverride || this.timezone,
          formatTimezone: this.timezoneOverride || this.timezone,
          allowInput: this.allowInput,
        };

        switch (this.mode) {
          case DatetimeMode.Date:
            break;
          case DatetimeMode.Time:
            window.AppConfig.mergeJson(config,
                {
                  noCalendar: true,
                  enableTime: true,
                }
            );
            break;
          case DatetimeMode.Datetime:
            window.AppConfig.mergeJson(config,
                {
                  enableTime: true,
                }
            );
            break;
          default:
            break;
        }

        this.fp = Flatpickr(this.$el, config);
        // To get clicking label to open up date picker
        const id = this.$el.getAttribute('id')
        this.$el.removeAttribute('id');
        this.fp.altInput.setAttribute('id', id);

        this.fpInput().addEventListener('blur', this.onBlur);
        this.fp.altInput.setAttribute('data-input-mask', this.maskType);
        this.fp.altInput.setAttribute('aria-required', this.required);
        this.fp.altInput.setAttribute('aria-describedby', this.describedBy);
        DatetimeHelper.initTabBehavior(config)
      },
      destroy() {
        if (!this.fp) return;
        this.fpInput().removeEventListener('blur', this.onBlur);
        this.fp.destroy();
        this.fp = null;
      },
      fpInput() {
        return this.fp.altInput || this.fp.input;
      },
      onBlur(event) {
        const currentValue = event.target.value;
        // arg: true triggers change event
        if (this.allowInput) this.fp.setDate(currentValue, true);
        this.$emit('blur', currentValue);
      },
      onInput(event) {
        this.emitDatetime(event.target.value);
      },
      setMinConfig(config, value) {
        config.minDate = value;
      },
      setMaxConfig(config, value) {
        config.maxDate = value;
      },
      getDate(datetime) {
        if (datetime && !isNaN(datetime.valueOf())) {
          return datetime.toISOString().slice(0, 10);
        } else {
          return;
        }
      },
      emitDatetime(datetime) {
        this.$emit('input', datetime);
      },
      resetDatetimeForDateMode() {
        // remove the time portion
        this.emitDatetime(this.getDate(new Date(this.$el.value)));
      },
      resetDatetimeForTimeMode() {
        // set by max or min so that the date portion is the same
        // as that for the other control
        // needed since min/max time functions based on date as well
        if (this.fp.config.maxDate) {
          this.emitDatetime(this.ISOEndOfDay(this.fp.config.maxDate));
        } else if (this.fp.config.minDate) {
          this.emitDatetime(this.ISOEndOfDay(this.fp.config.minDate));
        } else {
          this.emitDatetime(this.$el.value);
        }
      },
      resetDatetimeForDatetimeMode() {
        this.emitDatetime(this.$el.value);
      },
      resetDatetimeFor(mode) {
        switch (mode) {
          case DatetimeMode.Date:
            this.resetDatetimeForDateMode();
            break;
          case DatetimeMode.Time:
            this.resetDatetimeForTimeMode();
            break;
          case DatetimeMode.Datetime:
            this.resetDatetimeForDatetimeMode();
            break;
        }
        this.fpInput().placeholder = this.placeholderText;
      },
      ISOEndOfDay(datetime) {
        return moment(datetime).endOf('day').toISOString();
      }
    },
    computed: {
      altFormat: {
        get() {
          return DatetimeHelper.datePickerDisplayFormat(this.mode, this.timeFormat);
        }
      },
      ariaDateFormat: {
        get() {
          return DatetimeHelper.displayFormat(this.mode);
        }
      },
      placeholderText(): string {
        if (this.placeholder) {
          return this.placeholder;
        } else {
          switch (this.mode) {
            case DatetimeMode.Date:
              return 'MM/DD/YYYY';
            case DatetimeMode.Time:
              return 'HH:MM';
            case DatetimeMode.Datetime:
              return 'MM/DD/YYYY HH:MM';
          }
        }
      },
      maskType(): string {
        switch (this.mode) {
          case DatetimeMode.Date:
            return 'date';
          case DatetimeMode.Time:
            return 'time';
          case DatetimeMode.Datetime:
            return 'datetime';
        }
      },
      parseTimezone(): string {
        if (this.timezoneOverride) return this.timezoneOverride;
        return this.mode === DatetimeMode.Datetime ? this.timezone : "UTC";
      },
    },
    watch: {
      mode(value) {
        if (value in DatetimeMode) {
          this.destroy();
          this.mountPicker();
        }
        this.resetDatetimeFor(value);
      },
      min(value) {
        let config: Minimum = {minDate: null};
        if (value) this.setMinConfig(config, value);
        this.fp.set(config);
        if (!this.value) this.fp.jumpToDate(config.minDate) // jump to min date if min changes and no selection has been made
      },
      max(value) {
        let config: Maximum = {maxDate: null};
        if (value) this.setMaxConfig(config, value);
        this.fp.set(config);
      },
      value(newValue, oldValue) {
        if (newValue === oldValue) return;
        if (!this.fp) return

        if (newValue === null || newValue === undefined || newValue === "") {
          this.fp.clear()
          this.fp.jumpToDate(this.fp.config.minDate)
          $(this.fp.altInput).trigger('iMaskInput')
          return
        }
        this.fp.setDate(newValue, true)
      }
    }
  });
</script>
